import { useEffect, useRef, useState } from 'react';
import { DEFAULT_TEAM_COUNTRY, teamCountryToPlacesCode } from '@/constants/teamCountries';
import { loadGoogleMaps } from '@/lib/loadGoogleMaps';
import {
  parseGooglePlaceAddress,
  type ParsedPlaceAddress,
} from '@/lib/parseGooglePlaceAddress';

export interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (details: ParsedPlaceAddress) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  /** ISO 3166-1 alpha-2 country code for address suggestions. Default AU. */
  country?: string;
}

export function GooglePlacesAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  disabled = false,
  placeholder = 'Search for an address…',
  className = '',
  id,
  country = DEFAULT_TEAM_COUNTRY,
}: GooglePlacesAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const countryCode = teamCountryToPlacesCode(country);
  const countryRef = useRef(countryCode);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');

  onChangeRef.current = onChange;
  onPlaceSelectRef.current = onPlaceSelect;
  countryRef.current = countryCode;

  useEffect(() => {
    const el = inputRef.current;
    if (!el || document.activeElement === el) return;
    if (el.value !== value) {
      el.value = value;
    }
  }, [value]);

  useEffect(() => {
    let listener: google.maps.MapsEventListener | null = null;
    let cancelled = false;

    const bindPlaceListener = (autocomplete: google.maps.places.Autocomplete) => {
      listener?.remove();
      listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const parsed = parseGooglePlaceAddress(place);
        if (parsed.formattedAddress) {
          onChangeRef.current(parsed.formattedAddress);
          if (inputRef.current) {
            inputRef.current.value = parsed.formattedAddress;
          }
        }
        onPlaceSelectRef.current?.(parsed);
      });
    };

    const initAutocomplete = (google: typeof window.google) => {
      if (!inputRef.current || cancelled) return;

      if (autocompleteRef.current) {
        bindPlaceListener(autocompleteRef.current);
        setReady(true);
        setLoadError('');
        return;
      }

      const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: countryRef.current },
        types: ['address'],
        fields: ['formatted_address', 'address_components', 'geometry'],
      });
      autocompleteRef.current = autocomplete;
      bindPlaceListener(autocomplete);
      setReady(true);
      setLoadError('');
    };

    loadGoogleMaps(countryRef.current)
      .then((google) => {
        if (cancelled) return;
        initAutocomplete(google);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to load address suggestions.');
          setReady(false);
        }
      });

    return () => {
      cancelled = true;
      listener?.remove();
    };
  }, []);

  useEffect(() => {
    autocompleteRef.current?.setComponentRestrictions({ country: countryCode });
  }, [countryCode]);

  const inputClassName =
    className.trim().length > 0
      ? className
      : 'w-full bg-transparent text-gray-700 text-xs font-normal outline-none placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 disabled:text-gray-400';

  return (
    <div className="w-full min-w-0">
      <input
        ref={inputRef}
        id={id}
        type="text"
        defaultValue={value}
        onChange={(e) => {
          onChange(e.currentTarget.value);
        }}
        disabled={disabled}
        placeholder={ready ? placeholder : 'Loading address search…'}
        autoComplete="off"
        className={inputClassName}
      />
      {loadError ? <p className="text-red-500 text-[11px] mt-1">{loadError}</p> : null}
    </div>
  );
}
